import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { getSpendReport, getAllInsights, getLogs } from '../utils/api';
import { StatCard, Card, Grid, PageTitle, Loading, Badge } from '../components/UI';

export default function Overview() {
  const [spending, setSpending] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getSpendReport('last_7d'),
      getAllInsights('today'),
      getLogs({ limit: 5 }),
    ]).then(([sp, ci, lg]) => {
      setSpending(sp.data?.data);
      setCampaigns(ci.data?.data || []);
      setLogs(lg.data?.data?.logs || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const account = spending?.account || {};
  const campList = spending?.campaigns || campaigns || [];

  // Chart data — campaign spend
  const chartData = campList.slice(0, 8).map(c => ({
    name: (c.campaign_name || c.name || 'Campaign').slice(0, 12),
    spend: parseFloat(c.spend || 0),
    roas: parseFloat(c.purchase_roas?.[0]?.value || 0),
  }));

  const actionColor = { INCREASE: '#55efc4', DECREASE: '#ffd200', PAUSE: '#ff6584', KEEP: '#6b6b80' };

  return (
    <div>
      <PageTitle title="📊 Overview" sub="Agency performance at a glance" />

      <Grid cols={4}>
        <StatCard label="মোট Spend (৭ দিন)" value={`৳${parseFloat(account.spend || 0).toFixed(0)}`} color="#a29bfe" />
        <StatCard label="Active Campaigns" value={campList.filter(c => c.status !== 'PAUSED').length || campList.length} color="#55efc4" />
        <StatCard label="Avg ROAS" value={(campList.reduce((s, c) => s + parseFloat(c.purchase_roas?.[0]?.value || 0), 0) / (campList.length || 1)).toFixed(2)} color="#f7971e" />
        <StatCard label="Total Clicks" value={campList.reduce((s, c) => s + parseInt(c.clicks || 0), 0).toLocaleString()} color="#74b9ff" />
      </Grid>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>Campaign Spend (BDT)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fill: '#6b6b80', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b6b80', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 8 }} />
              <Bar dataKey="spend" fill="#6c63ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 16 }}>ROAS by Campaign</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fill: '#6b6b80', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b6b80', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#16161f', border: '1px solid #1e1e2e', borderRadius: 8 }} />
              <Bar dataKey="roas" fill="#43e97b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card>
        <div style={{ fontWeight: 700, marginBottom: 16 }}>🤖 Recent AI Decisions</div>
        {logs.length === 0 ? <div style={{ color: '#6b6b80', fontSize: 14 }}>কোনো AI decision নেই এখনো</div> : (
          logs.map((log, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #1e1e2e' }}>
              <span style={{ fontSize: 13, color: actionColor[log.action] || '#fff', fontWeight: 700, minWidth: 80 }}>{log.action}</span>
              <span style={{ fontSize: 13, flex: 1 }}>{log.campaignName}</span>
              <span style={{ fontSize: 12, color: '#6b6b80' }}>{log.reason}</span>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
